import { CountryCodeList, type CountryCode } from 'types/countries';
import type { SingleAddress } from '@zoocasa/go-search';
import { getAddressesBySlug } from 'data/addresses/go-search/fetcher';
import { getGoSearchHost } from 'utils/host-config';
import { defaultCACityPayload, defaultUSCityPayload } from 'constants/locations';
import type { TopMarket } from '../../local-focus/top-markets';
import { CA_CITY_FALLBACK_IMG, US_CITY_FALLBACK_IMG } from './consts';


// Go from most specific to least specific address. Pick the first address with valid coordinates.
export function pickLatLngFromAddresses(addresses: SingleAddress[]): { lat: number; lng: number } | null {
  for (let i = addresses.length - 1; i >= 0; i--) {
    const a = addresses[i] as SingleAddress & { latitude?: number; longitude?: number };
    if (
      typeof a.latitude === 'number' &&
      typeof a.longitude === 'number' &&
      a.latitude !== 0 &&
      a.longitude !== 0
    ) {
      return { lat: a.latitude, lng: a.longitude };
    }
  }
  return null;
}

export async function getCoordinatesForSlug(
  resolvedSlug: string,
  isCanadianSite: boolean
): Promise<{ latitude: number; longitude: number }> {
  const addresses = await getAddressesBySlug(resolvedSlug, undefined, getGoSearchHost(false));
  const coords = pickLatLngFromAddresses(addresses);
  const geoFallback = isCanadianSite ? defaultCACityPayload : defaultUSCityPayload;
  return {
    latitude: coords?.lat ?? geoFallback.latitude,
    longitude: coords?.lng ?? geoFallback.longitude,
  };
}

/** Build popular cities list for the city selector dropdown. If current city (user's last search) is not in popular cities list we append it. */
export function getPopularCitiesList(
  popularCitiesMarkets: TopMarket[],
  currentSlug: string,
  popularCityLabel: string,
  siteCountry: CountryCode
): TopMarket[] {
  const trimmed = currentSlug.trim();
  if (!trimmed) return popularCitiesMarkets;
  if (popularCitiesMarkets.some(m => m.slug === trimmed)) return popularCitiesMarkets;
  const isCanada = siteCountry === CountryCodeList.CANADA;
  return [
    ...popularCitiesMarkets,
    {
      name: popularCityLabel,
      slug: trimmed,
      thumbnailUrl: isCanada ? CA_CITY_FALLBACK_IMG : US_CITY_FALLBACK_IMG,
    },
  ];
}
